import {
  AlertDialog,
  Button,
  Chip,
  cn,
  Drawer,
  DrawerContent,
  TabItem,
  Tabs,
  TabsContent,
  TabsList,
  TextField,
  toast,
} from '@crabs-ai/ds-react';
import {hasSceneVoiceStage} from '@waka/host/scene-voice';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Close, Search } from '@crabs-ai/web-icons';
import TtsVoiceList from '@/shared/components/dialog/TtsDialog/TtsVoiceList';
import TtsVoiceDetail from '@/shared/components/dialog/TtsDialog/TtsVoiceDetail';
import TtsDialogMyVoiceTab from '@/shared/components/dialog/TtsDialog/TtsDialogMyVoiceTab';
import { TtsDialogProvider, TTSVoice, useTtsDialog } from './TtsDialogContext';
import type { GeminiPreviewLanguage } from '@/shared/components/dialog/TtsDialog/useGeminiPreviewUrl';
import { useTranslations } from 'next-intl';
import {
  type DtoVendor,
  type TTSEmotion,
  useGetTTSVoicesApi,
  type VideoEditorTTSVoice,
} from '@/shared/apis/tts/get-tts-voices';
import { isHighQualityTtsBlockedOnPlan } from '@/shared/user-plan/user-spec';
import { USER_PLAN_DISPLAY_NAME } from '@/shared/user-plan/user-spec';
import { useFeatureBlockedDialogStore } from '@/shared/store/feature-blocked-dialog-store';
import { useIsDesktop } from '@/shared/hooks';
import { LAYER } from '@/shared/constants/layer';
import { useFeatureEnabled } from '@/shared/hooks/useFeatureEnabled';
import {
  applyTtsVoiceFilters,
  filterTtsVoicesBySearch,
  isPremiumTtsVoice,
  sortTtsVoicesForPicker,
  splitRecommendedTtsVoices,
  type TtsVoiceFilterKey,
} from '@/shared/lib/tts-voice-display';
import { pickDefaultTtsCatalogVoice } from '@/shared/lib/tts-catalog-defaults';

export const DEFAULT_TTS_VOICE_FILTER_KEYS = ['all', 'male', 'female'] as const;
const EMPTY_RECOMMENDED_VOICE_CODES = [] as const;
const EMPTY_RECOMMENDED_VOICE_STYLE_BY_CODE = {} as const;

/** TtsDialog 상위 탭 — Phase B 단계 5 (MS-7). 'voice' 가 기존 동작 (default). */
export type TtsDialogTab = 'voice' | 'my-voice';

export interface TtsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultTts: TTSVoice | null;
  onConfirm: (voice: TTSVoice) => void;
  onCancel?: () => void;
  showPlanWarning?: boolean;
  planId?: number;
  /**
   * 다이얼로그 진입 시 활성 탭. Phase B 단계 5 신설.
   * 미전달 시 기존 동작 그대로 ('voice' 탭, 탭 UI 자체는 isOperator 게이팅).
   */
  defaultTab?: TtsDialogTab;
  /** my-voice 탭에서 사전 선택 시각 강조할 프리셋 id (BrandTemplate 진입점 등). */
  defaultPresetId?: number;
  /** 특정 vendor voice만 다이얼로그에 표시할 때 사용. */
  allowedVendors?: readonly DtoVendor[];
  voiceFilterKeys?: readonly TtsVoiceFilterKey[];
  recommendedVoiceCodes?: readonly string[];
  recommendedVoiceStyleByCode?: Readonly<Record<string, string>>;
  enableStyleDirective?: boolean;
  /**
   * preview 재생 언어 override. 미전달 시 카드가 UI locale 기준으로 결정(기존 동작).
   * MTS 처럼 UI locale ≠ 콘텐츠 언어인 경우 콘텐츠 언어 기반 값을 넘긴다.
   */
  previewLanguage?: GeminiPreviewLanguage;
}

function TtsDialogContent({
  open,
  setOpen,
  onConfirm,
  onCancel,
  showPlanWarning = false,
  planId,
  defaultTab,
  defaultPresetId,
  defaultTts,
  allowedVendors,
  voiceFilterKeys,
  recommendedVoiceCodes = EMPTY_RECOMMENDED_VOICE_CODES,
  recommendedVoiceStyleByCode = EMPTY_RECOMMENDED_VOICE_STYLE_BY_CODE,
  enableStyleDirective,
}: TtsDialogProps) {
  const t = useTranslations();
  const isDesktop = useIsDesktop();
  const { data: voicesData, isLoading, error } = useGetTTSVoicesApi();
  // my-voice 탭과 Gemini 고도화 옵션은 오퍼레이터에게만 노출 (단계적 출시).
  // Gemini voice 자체는 일반 사용자에게도 노출한다.
  const { enable: isOperator } = useFeatureEnabled();
  const canUseStyleDirective = enableStyleDirective ?? isOperator;
  const [selectedFilterKey, setSelectedFilterKey] =
    useState<TtsVoiceFilterKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState<TtsDialogTab>(
    defaultTab ?? 'voice',
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerContainer, setDrawerContainer] = useState<HTMLElement | null>(
    null,
  );
  const handleDialogContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      setDrawerContainer(node);
    },
    [],
  );

  const { setDialogData } = useFeatureBlockedDialogStore();

  // Context에서 상태 가져오기
  const {
    selectedVoice,
    setSelectedVoice,
    setSelectedEmotion,
    tempo,
    setTempo,
    selectedEmotion,
    pitch,
    setStyle,
    style,
    stopCurrentAudio,
  } = useTtsDialog();

  // 다이얼로그가 닫힐 때 재생 중인 오디오 중지 및 Drawer 닫기
  useEffect(() => {
    if (!open) {
      stopCurrentAudio();
      setIsDrawerOpen(false);
    }
  }, [open, stopCurrentAudio]);

  useEffect(() => {
    return () => {
      stopCurrentAudio();
    };
  }, [stopCurrentAudio]);

  // API 응답에서 실제 보이스 배열 추출. Gemini voice 자체는 권한과 무관하게 노출한다.
  const voices = useMemo(() => {
    const rawVoices = voicesData?.data ?? [];
    if (!allowedVendors || allowedVendors.length === 0) return rawVoices;
    const allowedVendorSet = new Set(allowedVendors);
    return rawVoices.filter((voice) => allowedVendorSet.has(voice.vendor));
  }, [allowedVendors, voicesData]);

  const visibleFilterKeys = useMemo<readonly TtsVoiceFilterKey[]>(() => {
    if (!voiceFilterKeys || voiceFilterKeys.length === 0) {
      return DEFAULT_TTS_VOICE_FILTER_KEYS;
    }
    return voiceFilterKeys;
  }, [voiceFilterKeys]);
  const hasAllFilter = visibleFilterKeys.includes('all');
  const concreteFilterKeys = useMemo(
    () => visibleFilterKeys.filter((filterKey) => filterKey !== 'all'),
    [visibleFilterKeys],
  );
  const filterScopeKeys = useMemo(
    () => getAllVoiceScopeFilterKeys(voiceFilterKeys, concreteFilterKeys),
    [concreteFilterKeys, voiceFilterKeys],
  );
  const shouldShowVoiceFilters = !(
    visibleFilterKeys.length <= 1 ||
    (hasAllFilter && concreteFilterKeys.length <= 1)
  );

  // open 시 defaultTab/filter/search 로 재진입 — 같은 인스턴스가 다른 caller 로 재오픈될 때 의도 반영.
  // stopCurrentAudio 미호출 의도: close 시 stopCurrentAudio 가 이미 처리.
  useEffect(() => {
    if (!open) return;
    setCurrentTab(defaultTab ?? 'voice');
    setSearchQuery('');
    setSelectedFilterKey(getInitialSelectedVoiceFilterKey(visibleFilterKeys));
  }, [defaultTab, open, visibleFilterKeys]);

  useEffect(() => {
    if (!open) return;
    setSelectedFilterKey((currentFilterKey) => {
      if (visibleFilterKeys.includes(currentFilterKey)) return currentFilterKey;
      return getInitialSelectedVoiceFilterKey(visibleFilterKeys);
    });
  }, [open, visibleFilterKeys]);

  // voices 로드 후 BE catalog(defaultUseCases)가 지정한 기본 보이스를 자동으로 선택한다.
  // 구 catalog 응답에는 defaultUseCases가 없을 수 있어, 범용 다이얼로그에서는 shared helper의 legacy fallback만 허용한다.
  useEffect(() => {
    if (voices.length === 0) {
      return;
    }

    if (!selectedVoice) {
      const defaultVoice = pickDefaultTtsCatalogVoice(voices);
      if (defaultVoice) {
        setSelectedVoice(defaultVoice);
      }
      return;
    }

    const updatedVoice = voices.find(
      (voice) => voice.code === selectedVoice.code,
    );

    if (!updatedVoice) {
      // 저장된 selectedVoice 가 목록에서 사라짐(스페이스 차단 보이스 지정, vendor 필터 등으로
      // 조회 목록에서 제외된 경우 등) — 기본값으로 전환. 서버 저장 데이터는 무변경, 화면 선택만 전환한다.
      const defaultVoice = pickDefaultTtsCatalogVoice(voices);
      if (defaultVoice) {
        setSelectedVoice(defaultVoice);
      }
      return;
    }

    if (updatedVoice !== selectedVoice) {
      setSelectedVoice(updatedVoice);
    }
  }, [voices, selectedVoice, setSelectedVoice]);

  const filteredVoices = useMemo(() => {
    const effectiveFilterKeys =
      selectedFilterKey === 'all' ? filterScopeKeys : [selectedFilterKey];
    const genderFilteredVoices = applyTtsVoiceFilters(
      voices,
      effectiveFilterKeys,
    );
    const searchedVoices = filterTtsVoicesBySearch(
      genderFilteredVoices,
      searchQuery,
    );
    return sortTtsVoicesForPicker(searchedVoices);
  }, [filterScopeKeys, searchQuery, selectedFilterKey, voices]);

  const { recommended: recommendedVoices, remaining: remainingVoices } =
    useMemo(
      () => splitRecommendedTtsVoices(filteredVoices, recommendedVoiceCodes),
      [filteredVoices, recommendedVoiceCodes],
    );

  const descriptionNode = useMemo(() => {
    if (showPlanWarning) {
      const planWarning = t('Common.component.dialog.tts-dialog.plan-warning');
      return (
        <span className="text-sys-cont-negative-default">{planWarning}</span>
      );
    }

    return null;
  }, [showPlanWarning, t]);

  const isPlanBlockingHighQuality = useMemo(() => {
    if (!planId) return false;
    return isHighQualityTtsBlockedOnPlan(planId);
  }, [planId]);

  const handleVoiceClick = (voice: VideoEditorTTSVoice) => {
    const isHighQualityVoice = isPremiumTtsVoice(voice);

    if (isPlanBlockingHighQuality && isHighQualityVoice) {
      setDialogData({
        open: true,
        planNameOverride: `${USER_PLAN_DISPLAY_NAME['starter']} / ${USER_PLAN_DISPLAY_NAME['pro']} / ${USER_PLAN_DISPLAY_NAME['proPlus']} / ${USER_PLAN_DISPLAY_NAME['ultra']}`,
      });
      return;
    }

    if(hasSceneVoiceStage()&&canUseStyleDirective&&style.trim()&&voice.vendor!=='gemini')
      toast({type:'error',description:'다른 제공자의 목소리를 선택하여 Gemini 스타일 지시가 해제됩니다.'});

    const prevVendor = selectedVoice?.vendor;
    setSelectedVoice(voice);
    setSelectedEmotion(voice.emotions[0] ?? null);
    setStyle(
      canUseStyleDirective && voice.vendor === 'gemini'
        ? (recommendedVoiceStyleByCode[voice.code] ?? '')
        : '',
    );

    // 벤더가 변경되면 해당 벤더의 기본 템포로 리셋, 같은 벤더면 범위 클램핑
    if (prevVendor !== voice.vendor) {
      setTempo(voice.tempoDefault);
    } else {
      const clampedTempo = Math.min(
        Math.max(tempo, voice.tempoMin),
        voice.tempoMax,
      );
      if (clampedTempo !== tempo) {
        setTempo(clampedTempo);
      }
    }

    // 모바일에서는 Drawer 열기
    if (!isDesktop) {
      setIsDrawerOpen(true);
    }
  };

  // my-voice 탭은 isOperator 단계 출시 — false 면 탭 UI 자체 미노출 (기존 caller 시각 변화 없음).
  const showTabBar = isOperator;
  const isMyVoiceTab = showTabBar && currentTab === 'my-voice';

  const handleConfirm = () => {
    // my-voice 탭은 카드 클릭 즉시 onConfirm 하므로 footer Confirm 으로 진입 시
    // voice 탭의 selectedVoice 가 의도치 않게 적용되는 fall-through 차단.
    if (isMyVoiceTab) return;
    if (selectedVoice) {
      // A hidden style control must not erase an existing native scene directive.
      const trimmedStyle = selectedVoice.vendor !== 'gemini' ? ''
        : canUseStyleDirective ? style.trim()
        : hasSceneVoiceStage() && selectedVoice.code === defaultTts?.voice?.code
          ? defaultTts.style?.trim() ?? '' : '';
      onConfirm({
        voice: selectedVoice,
        tempo: tempo,
        pitch: selectedVoice.pitchMin != null ? pitch : null,
        emotion: selectedEmotion as TTSEmotion,
        style: trimmedStyle || undefined,
      });
      setOpen(false);
    }
  };

  // my-voice 탭 카드 클릭 → preset payload → TTSVoice 변환 → 즉시 onConfirm + 닫기.
  const handleMyVoicePick = useCallback(
    (voice: TTSVoice) => {
      onConfirm(voice);
      setOpen(false);
    },
    [onConfirm, setOpen],
  );

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setOpen(false);
    }
  };

  // 탭 전환 시 다른 탭의 audio 가 살아있을 race 차단.
  // - voice → my-voice: voice 탭 audio 정지 (Provider) — my-voice hook 은 컴포넌트 mount 시 새로 시작
  // - my-voice → voice: TtsDialogMyVoiceTab unmount 시 useVoicePresetPreview cleanup 으로 정지됨
  const handleTabChange = useCallback(
    (value: string) => {
      stopCurrentAudio();
      setCurrentTab(value as TtsDialogTab);
    },
    [stopCurrentAudio],
  );

  const handleVoiceFilterClick = useCallback((filterKey: TtsVoiceFilterKey) => {
    setSelectedFilterKey(filterKey);
  }, []);

  const isVoiceFilterChipSelected = useCallback(
    (filterKey: TtsVoiceFilterKey) => {
      return selectedFilterKey === filterKey;
    },
    [selectedFilterKey],
  );

  const myVoiceTabContent = (
    <TtsDialogMyVoiceTab
      visibleVoices={voices}
      onPick={handleMyVoicePick}
      defaultPresetId={defaultPresetId}
    />
  );

  const voiceControls = (
    <div className="flex flex-col gap-2xs">
      {shouldShowVoiceFilters && (
        <div className="flex flex-wrap items-center gap-2xs">
          {visibleFilterKeys.map((filterKey) => (
            <Chip
              key={filterKey}
              labelNode={t(
                `Common.component.dialog.tts-dialog.filter.${filterKey}`,
              )}
              selected={isVoiceFilterChipSelected(filterKey)}
              onClick={() => handleVoiceFilterClick(filterKey)}
            />
          ))}
        </div>
      )}
      <TextField
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder={t('Common.component.dialog.tts-dialog.search-placeholder')}
        isShowLeadingIcon
        leadingIconNode={<Search />}
      />
    </div>
  );

  const voiceSections = (
    <div className="flex flex-col gap-xs">
      {recommendedVoices.length > 0 && (
        <section className="flex flex-col gap-2xs">
          <h3 className="text-title-small text-sys-cont-neutral-default">
            {t('Common.component.dialog.tts-dialog.recommended')}
          </h3>
          <TtsVoiceList
            voices={recommendedVoices}
            onVoiceClick={handleVoiceClick}
            emptyText={t('Common.component.dialog.tts-dialog.no-results')}
          />
        </section>
      )}
      <section className="flex flex-col gap-2xs">
        <h3 className="text-title-small text-sys-cont-neutral-default">
          {t('Common.component.dialog.tts-dialog.all-voices')}
        </h3>
        <TtsVoiceList
          voices={remainingVoices}
          onVoiceClick={handleVoiceClick}
          emptyText={t('Common.component.dialog.tts-dialog.no-results')}
        />
      </section>
    </div>
  );

  // voice 탭의 chip + list/detail 내부 컨텐츠. desktop/mobile 모두에서 재사용.
  const desktopVoiceContent = selectedVoice ? (
    <>
      {voiceControls}
      {/* list/detail wrapper — flex-1 + min-h-0 로 잔여 공간 점유 */}
      <div className="relative flex min-h-0 w-full flex-1">
        <div className="absolute flex h-full w-full gap-md">
          <div className="flex-1 overflow-auto">{voiceSections}</div>
          <div
            className={cn(
              'flex h-full min-h-0 flex-col gap-2xs overflow-y-auto rounded-2xs bg-sys-style-neutral-lightest p-sm transition-all',
              'flex-1',
            )}
          >
            <TtsVoiceDetail enableStyleDirective={canUseStyleDirective} />
          </div>
        </div>
      </div>
    </>
  ) : null;

  const mobileVoiceContent = selectedVoice ? (
    <>
      {voiceControls}
      <div className="flex-1 overflow-y-auto">{voiceSections}</div>
    </>
  ) : null;

  // 데스크탑 뷰
  if (isDesktop) {
    return (
      <>
        <AlertDialog
          open={open}
          setOpen={setOpen}
          titleNode={t('Common.component.dialog.tts-dialog.title')}
          descriptionNode={descriptionNode ?? undefined}
          cancelText={t('Common.component.dialog.tts-dialog.cancel')}
          onCancel={handleCancel}
          confirmText={t('Common.component.dialog.tts-dialog.confirm')}
          confirmButtonState={isMyVoiceTab ? 'disabled' : undefined}
          onConfirm={handleConfirm}
          contentClassName=" flex h-full max-h-[calc(100%-80*2px)] w-full max-w-[calc(100%-80*2px)] flex-col"
        >
          {/* min-h-0: flex column chain 의 height 제한이 자식 min-height: auto 기본값으로 무력화되지 않도록.
              showTabBar 분기:
                - true (operator): Tabs wrapper 가 outer 역할. 로딩/에러 중에도 TabsList 노출 (기존 동작 유지).
                - false (비-operator): 탭 UI 미노출, voice 컨텐츠만 단일 div 유지. */}
          <div className="flex h-full min-h-0 w-full flex-col gap-xs">
            {showTabBar ? (
              // ds-react Tabs 가 role/aria-selected/aria-controls/keyboard 를 자동 위임.
              // keepMounted=false (default) → unmount 시 useVoicePresetPreview cleanup 으로 audio 정지 (race 차단).
              <Tabs
                value={currentTab}
                onValueChange={handleTabChange}
                size="md"
                className="flex min-h-0 flex-1 flex-col gap-xs"
              >
                <TabsList
                  aria-label={t(
                    'Common.component.dialog.tts-dialog.tab.aria-label',
                  )}
                >
                  <TabItem value="voice">
                    {t('Common.component.dialog.tts-dialog.tab.voice')}
                  </TabItem>
                  <TabItem value="my-voice">
                    {t('Common.component.dialog.tts-dialog.tab.my-voice')}
                  </TabItem>
                </TabsList>

                {/* 로딩 상태 */}
                {isLoading && (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <p className="text-sys-cont-neutral-default">
                        보이스 목록을 불러오는 중...
                      </p>
                    </div>
                  </div>
                )}

                {/* 에러 상태 */}
                {error && !isLoading && (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <p className="text-sys-cont-negative-default">
                        보이스 목록을 불러오는데 실패했습니다.
                      </p>
                      <p className="text-sys-cont-neutral-light">
                        잠시 후 다시 시도해주세요.
                      </p>
                    </div>
                  </div>
                )}

                {!isLoading && !error && (
                  <>
                    {/* min-h-0 필수: flex 자식 min-height: auto 기본값으로 인해 card 그리드가 부모 max-h 넘어 새는 회귀 차단 */}
                    <TabsContent
                      value="my-voice"
                      className="min-h-0 flex-1 overflow-auto"
                    >
                      {myVoiceTabContent}
                    </TabsContent>
                    {/* voice TabsContent 는 selectedVoice 가 있을 때만 렌더.
                        panel 미존재 시 PR1 의 panelValues 가 자동 unregister → TabItem voice aria-controls=undefined (dangling 회피). */}
                    {selectedVoice && (
                      <TabsContent
                        value="voice"
                        className="flex min-h-0 flex-1 flex-col gap-xs"
                      >
                        {desktopVoiceContent}
                      </TabsContent>
                    )}
                  </>
                )}
              </Tabs>
            ) : (
              <>
                {/* 로딩 상태 */}
                {isLoading && (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <p className="text-sys-cont-neutral-default">
                        보이스 목록을 불러오는 중...
                      </p>
                    </div>
                  </div>
                )}

                {/* 에러 상태 */}
                {error && !isLoading && (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <p className="text-sys-cont-negative-default">
                        보이스 목록을 불러오는데 실패했습니다.
                      </p>
                      <p className="text-sys-cont-neutral-light">
                        잠시 후 다시 시도해주세요.
                      </p>
                    </div>
                  </div>
                )}

                {!isLoading && !error && selectedVoice && (
                  <div className="flex h-full w-full flex-col gap-xs">
                    {desktopVoiceContent}
                  </div>
                )}
              </>
            )}
          </div>
        </AlertDialog>
      </>
    );
  }

  // 모바일 뷰
  if (!open) return null;

  const mobileViewContent = (
    <>
      {/* 전체 화면 컨텐츠 */}
      <div
        ref={handleDialogContainerRef}
        className="fixed inset-0 flex flex-col bg-sys-bg-surface"
        style={{ zIndex: LAYER.DIALOG }}
      >
        {/* 상단 헤더 */}
        <div className="flex min-h-[64px] items-center gap-2xs border-b border-sys-style-neutral-light px-md py-xs">
          <Close
            className="size-[24px] cursor-pointer [&_path]:fill-sys-cont-neutral-default"
            onClick={handleCancel}
          />
          <h2 className="flex-1 text-title-xlarge text-sys-cont-neutral-default">
            {t('Common.component.dialog.tts-dialog.title')}
          </h2>
        </div>

        {/* 설명 영역 */}
        {descriptionNode && (
          <div className="px-md py-xs">
            <div className="text-body-medium text-sys-cont-neutral-default">
              {typeof descriptionNode === 'string' ? (
                descriptionNode
              ) : (
                <>{descriptionNode}</>
              )}
            </div>
          </div>
        )}

        {/* TTS 리스트 영역 — showTabBar 분기:
            - true (operator): Tabs wrapper 가 TabsList + TabsContent 를 모두 감싸 ds-react 자동 ARIA 작동
            - false (비-operator): 탭 UI 미노출, voice 컨텐츠만 단일 div 로 유지 (시각 변화 0)
        */}
        {showTabBar ? (
          // keepMounted=false (default) → unmount 시 useVoicePresetPreview cleanup 으로 audio 정지 (race 차단).
          <Tabs
            value={currentTab}
            onValueChange={handleTabChange}
            size="md"
            className="flex flex-1 flex-col gap-xs overflow-hidden"
          >
            <div className="px-md">
              <TabsList
                aria-label={t(
                  'Common.component.dialog.tts-dialog.tab.aria-label',
                )}
              >
                <TabItem value="voice">
                  {t('Common.component.dialog.tts-dialog.tab.voice')}
                </TabItem>
                <TabItem value="my-voice">
                  {t('Common.component.dialog.tts-dialog.tab.my-voice')}
                </TabItem>
              </TabsList>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-xs overflow-hidden px-md">
              {/* 로딩 상태 */}
              {isLoading && (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center">
                    <p className="text-sys-cont-neutral-default">
                      보이스 목록을 불러오는 중...
                    </p>
                  </div>
                </div>
              )}

              {/* 에러 상태 */}
              {error && !isLoading && (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center">
                    <p className="text-sys-cont-negative-default">
                      보이스 목록을 불러오는데 실패했습니다.
                    </p>
                    <p className="text-sys-cont-neutral-light">
                      잠시 후 다시 시도해주세요.
                    </p>
                  </div>
                </div>
              )}

              {!isLoading && !error && (
                <>
                  {/* my-voice 탭 — min-h-0 로 부모 flex max-h 내 스크롤 보장 */}
                  <TabsContent
                    value="my-voice"
                    className="min-h-0 flex-1 overflow-y-auto"
                  >
                    {myVoiceTabContent}
                  </TabsContent>
                  {/* voice TabsContent 는 selectedVoice 가 있을 때만 렌더 — desktop 과 동일 (M-1 회피). */}
                  {selectedVoice && (
                    <TabsContent
                      value="voice"
                      className="flex min-h-0 flex-1 flex-col gap-xs overflow-hidden"
                    >
                      {mobileVoiceContent}
                    </TabsContent>
                  )}
                </>
              )}
            </div>
          </Tabs>
        ) : (
          <div className="flex flex-1 flex-col gap-xs overflow-hidden px-md">
            {/* 로딩 상태 */}
            {isLoading && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-sys-cont-neutral-default">
                    보이스 목록을 불러오는 중...
                  </p>
                </div>
              </div>
            )}

            {/* 에러 상태 */}
            {error && !isLoading && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-sys-cont-negative-default">
                    보이스 목록을 불러오는데 실패했습니다.
                  </p>
                  <p className="text-sys-cont-neutral-light">
                    잠시 후 다시 시도해주세요.
                  </p>
                </div>
              </div>
            )}

            {!isLoading && !error && selectedVoice && (
              <div className="flex flex-1 flex-col gap-xs overflow-hidden">
                {mobileVoiceContent}
              </div>
            )}
          </div>
        )}

        {/* 하단 완료 버튼 — voice 탭 전용 (my-voice 는 카드 클릭 즉시 close) */}
        {!isMyVoiceTab && selectedVoice && (
          <div className="border-t border-sys-style-neutral-light px-md py-sm">
            <Button
              className="w-full"
              hierarchy="primary"
              onClick={handleConfirm}
            >
              {t('Common.component.dialog.tts-dialog.confirm')}
            </Button>
          </div>
        )}
      </div>

      {/* Drawer - 보이스 옵션 선택 (voice 탭 전용) */}
      <Drawer
        container={drawerContainer ?? undefined}
        open={isDrawerOpen && !isMyVoiceTab}
        onOpenChange={setIsDrawerOpen}
      >
        <DrawerContent className="flex flex-col">
          <div className="flex flex-col gap-2xs overflow-y-auto px-md py-xs">
            <TtsVoiceDetail enableStyleDirective={canUseStyleDirective} />
          </div>
          {/* 하단 완료 버튼 */}
          {selectedVoice && (
            <div className="border-t border-sys-style-neutral-light px-md py-sm">
              <Button
                className="w-full"
                hierarchy="primary"
                onClick={() => {
                  handleConfirm();
                  setIsDrawerOpen(false);
                }}
              >
                {t('Common.component.dialog.tts-dialog.confirm')}
              </Button>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );

  // Portal을 사용하여 body에 직접 렌더링 (부모 사이즈 무시)
  return typeof window !== 'undefined'
    ? createPortal(mobileViewContent, document.body)
    : null;
}

export default function TtsDialog(props: TtsDialogProps) {
  return (
    <TtsDialogProvider
      initialVoice={props.defaultTts}
      previewLanguage={props.previewLanguage}
    >
      <TtsDialogContent {...props} />
    </TtsDialogProvider>
  );
}

function getInitialSelectedVoiceFilterKey(
  visibleFilterKeys: readonly TtsVoiceFilterKey[],
): TtsVoiceFilterKey {
  if (visibleFilterKeys.includes('all')) return 'all';
  return visibleFilterKeys[0] ?? 'all';
}

function getAllVoiceScopeFilterKeys(
  voiceFilterKeys: readonly TtsVoiceFilterKey[] | undefined,
  concreteFilterKeys: readonly TtsVoiceFilterKey[],
): readonly TtsVoiceFilterKey[] {
  if (!voiceFilterKeys || voiceFilterKeys.length === 0) return ['all'];
  if (concreteFilterKeys.length > 0) return concreteFilterKeys;
  if (voiceFilterKeys.includes('all')) return ['all'];
  return voiceFilterKeys.slice(0, 1);
}
